import { ArrowLeftOutlined } from '@ant-design/icons'
import {  
  Button,
  Spin,
  Table,
  TablePaginationConfig,
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc';
import html2canvas from 'html2canvas'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { SimpleChart } from '../../../../components/Charts/SimpleChart'
import { useFullScreenContainerContext } from '../../../../components/FullScreenContainer/FullScreenContainer'
import {
  selectPatientRecordedDataByKey,
} from '../../../../state/recordedData/recordedDataReducer'
import { useLazyGetRecordedEcgSignalsQuery } from '../../../../state/recordedEcgSignals/api'
import {
  getPatientRecordedEcgWindow,
  selectPatientRecordedEcgWindowByKey,
} from '../../../../state/recordedEcgWindow/recordedEcgWindowReducer'
import { PatientRecordedEcgWindowType } from '../../../../state/recordedEcgWindow/types'
import { selectPatientByKey } from '../../../../state/patient/patientReducer'
import { selectHospitalName } from '../../../../state/auth/authReducer'
import {
  EcgChartWrapper,
  EcgChartsContainer,
  EcgContentContainer,
  EcgHeaderContainer,
  LoadingContainer,
  LoadingContent,
  LoadingText,
  RecordedDataTabContainer,
  TimeContainer,
} from './RecordedEcgTabStyle'

import DurationSelector from '../RecordedVitalsTab/DurationSelector';
import MoreOptionsMenu from './MoreOptionsMenu';

type RecordedEcgTabProp = {
  patientKey: string
}

dayjs.extend(utc);

export default function RecordedEcgTab({ patientKey }: RecordedEcgTabProp) {

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ecgView, setEcgView] = useState<'window' | 'signal'>('window')
  const [selectedWindow, setSelectedWindow] =
    useState<PatientRecordedEcgWindowType | null>(null)

  const recordedData = useSelector(selectPatientRecordedDataByKey(patientKey))?.patientRecordedData;
  const patientData = useSelector(selectPatientByKey(patientKey))
  const [selectedDuration, setSelectedDuration] = useState({
    key: 'latest',
    startTime: 0,
    endTime: 0,
    isCustom: false,
  });

  const durationSelectorRef = useRef<{ handleMenuClick: (arg: { key: string }) => void } | null>(null);

  const hospitalName = useSelector(selectHospitalName)
 

  const selectedRecordedEcgWindow = useSelector(
    selectPatientRecordedEcgWindowByKey(patientKey)
  )

  const patientRecordedEcgWindowLength = useMemo(() => {
    return selectedRecordedEcgWindow?.patientRecordedEcgWindow.length || 0;
  }, [selectedRecordedEcgWindow?.patientRecordedEcgWindow]);

  const [trigger, result] = useLazyGetRecordedEcgSignalsQuery()

  const signals = useMemo(() => {
    if (result.isFetching) return []
    const resultSignals: any = result.data ? result.data.signals.map(({ position, value }) => (value / 1000)) : []
    console.log('resultSignals ', resultSignals)
    return resultSignals;
  }, [result])

  useEffect(() => {
    if(selectedDuration.key ) {
      triggerSelection(selectedDuration.key);
    }    
  }, [selectedDuration])

  const handleRefresh = () => {
    triggerSelection(selectedDuration.key);
  };

  const triggerSelection = (key: string) => {
    if (durationSelectorRef.current) {
      durationSelectorRef.current.handleMenuClick({ key });
    }
  };


  const { setFullScreenTitle } = useFullScreenContainerContext()

  const onGoBack = () => {
    setEcgView('window')
  }


  const windowColumns = (currentPage: number, pageSize: number): ColumnsType<PatientRecordedEcgWindowType> => [
    {
      title: 'Episode Snapshot',
      key: 'index',
      render: (text, record, index) => `ECG Snapshot-${patientRecordedEcgWindowLength - ((currentPage - 1) * pageSize + index)}`,
    },
    { title: 'Snapshot Start Time', dataIndex: 'start_time', key: 'start_time', render: (text) => dayjs.unix((text / 1000)).format('DD-MMM-YY HH:mm:ss') },
    { title: 'Snapshot End Time', dataIndex: 'end_time', key: 'end_time', render: (text) => dayjs.unix((text / 1000)).format('DD-MMM-YY HH:mm:ss') },
  ];


  const generatePatientDetails = (patientData: any, selectedWindow: any): any[] => {
    return [
      { label: 'Patient Name', value: `${patientData?.title || ''} ${patientData?.firstName || ''} ${patientData?.lastName || ''}` },
      { label: 'DOB', value: dayjs(patientData?.dob).format('DD-MMM-YY') || 'N/A' },
      { label: 'Age', value: patientData?.age || 'N/A' },
      { label: 'Doctor', value: patientData?.assignedDoctor || 'N/A' },
      { label: 'Episode Snapshot', value: sessionStorage.getItem('selected-ECG-SnapShotId') || 'N/A' },
      { label: 'Snapshot Start Time', value: selectedWindow ? dayjs.unix(selectedWindow.start_time / 1000).format('DD-MMM-YY HH:mm:ss') : 'N/A' },
      { label: 'Snapshot End Time', value: selectedWindow ? dayjs.unix(selectedWindow.end_time / 1000).format('DD-MMM-YY HH:mm:ss') : 'N/A' }
    ];
  };

  const generateHTMLContent = (fileName: any, patientDetails: any, imgData: any, hospitalName: any): any => `
  <!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${fileName}</title>
      <style>
          @page { margin: 0; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; display: flex; flex-direction: column; align-items: center; font-size: 14px; background-color: #f4f7f9; color: #333; }
          .header { width: 100%; background: linear-gradient(135deg, #37474F, #607D8B); color: white; text-align: center; padding: 20px 0; position: fixed; top: 0; left: 0; font-size: 24px; font-weight: bold; text-transform: uppercase; box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.3), 0px 3px 6px rgba(0, 0, 0, 0.2) inset; text-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);}
          .content { width: 90%; display: flex; flex-direction: column; align-items: center; margin-top: 100px; }
          .details-container { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; width: 100%; margin-top: 15px; }
          .details { background-color: white; padding: 12px; border-radius: 8px; box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1); }
          .chart { width: 100%; display: flex; align-items: center; justify-content: center; height: 50vh; }
          .chart img { max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1); }
          .footer { width: 90%; max-width: 800px; display: flex; justify-content: center; align-items: center; padding: 12px 20px; background-color: white; border-top: 2px solid #ddd; position: fixed; bottom: 0; left: 50%; transform: translateX(-50%); text-align: center; font-size: 13px; color: #555; box-shadow: 0px -2px 5px rgba(0, 0, 0, 0.1); border-radius: 8px 8px 0 0; }
          .footer-content { display: flex; justify-content: space-between; width: 100%; }
          .footer-left { text-align: left; }
          .footer-right { text-align: right; }
      </style>
  </head>
  <body>
      <div class="header">ECG Report</div>
      <div class="content">
          <div class="details-container">
    ${patientDetails.map((detail: any, index: any) => { let cell = index === 4 ? 6 : index + (index > 4 ? 1 : 0); return `<div class="details" style="grid-column: ${(cell % 3) + 1}; grid-row: ${Math.floor(cell / 3) + 1};">${detail.label.toString().trim() && detail.value.toString().trim() ? `<strong>${detail.label}:</strong> ${detail.value}` : ''}</div>`; }).join('')}
</div>
          <div class="chart"><img src="${imgData}" alt="ECG Chart" /></div>
      </div>
      <div class="footer">
          <div class="footer-content">
              <div class="footer-left">Printed on ${new Date().toLocaleString()}</div>
              <div class="footer-right">${hospitalName}</div>
          </div>
      </div>
  </body>
  </html>`;

  const openPrintWindow = (content: any, fileName: any): void => {
    const printWindow = window.open('', '_blank', 'width=900,height=600');
    if (!printWindow) {
      alert('Popup blocked! Please allow popups for this site.');
      return;
    }
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 50);
  };

  const handlePrint = (): void => {
    const printableSignals = document.getElementById('printable-signals');
    if (!printableSignals) return;

    html2canvas(printableSignals, { scale: 2 })
      .then((canvas: any) => {
        const imgData = canvas.toDataURL('image/png');
        const patientDetails = generatePatientDetails(patientData, selectedWindow);
        const formattedSnapshotEndTime = selectedWindow ? dayjs.unix(selectedWindow.end_time / 1000).format('DD-MMM-YY HH:mm:ss') : 'N/A';
        const fileName = `${patientData?.firstName}_${patientData?.lastName}_ECG_Report_${formattedSnapshotEndTime}`;
        const htmlContent = generateHTMLContent(fileName, patientDetails, imgData, hospitalName);
        openPrintWindow(htmlContent, fileName);
      })
      .catch((error: any) => {
        console.error('Error generating print preview:', error);
        alert('An error occurred while preparing the print. Please try again.');
      });
  };

  const isDisableButton = useMemo(() => {
    return (
      !patientKey ||
      selectedDuration.startTime === 0 ||
      selectedDuration.endTime === 0 ||
      !recordedData ||
      recordedData.length === 0
    )
  }, [selectedDuration, patientKey, recordedData])

  useEffect(() => {
    setEcgView('window')
  }, []);

  useEffect(() => {
    setFullScreenTitle(
      <>
        <Button
          type='primary'
          style={{ display: ecgView === 'signal' ? 'block' : 'none', marginRight: 'auto' }}
          icon={<ArrowLeftOutlined />}
          onClick={onGoBack}
        >
          Go Back
        </Button>
      </>

    )

  }, [isDisableButton, setFullScreenTitle, ecgView])

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };


  return (
    <RecordedDataTabContainer style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', fontSize: '1.2rem' }}>
        <MoreOptionsMenu
          onPrint={handlePrint}
          onRefresh={handleRefresh}
          disablePrint={ecgView !== 'signal' ? true : false}
          disableRefresh={selectedDuration.isCustom || (ecgView !== 'signal' ? false : true)}
        />

        &nbsp;&nbsp;
        <h2 style={{ marginTop: '0.5rem' }}>Recorded ECG</h2> &nbsp;&nbsp;&nbsp;
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <span
          className='timeLabel'
          style={{ fontWeight: 'bold' }}
        >
          Duration:&nbsp;&nbsp;&nbsp;
        </span>
        <DurationSelector
          key="ecgRecorded"
          disabled={ecgView === 'signal'}
          dispatchMethod={getPatientRecordedEcgWindow}
          patientKey={patientKey}
          onChange={(duration) => {
            setSelectedDuration(duration);
          }}
        />
      </div>
      <hr style={{ width: '100%', backgroundColor: 'grey' }} />
      <TimeContainer style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '2.5rem' }}>
        <div>
          <span className='timeLabel'>Start Time: </span>
          <span style={{ fontSize: '1rem' }}>
            {dayjs(selectedDuration.startTime).format('DD-MMM-YY HH:mm:ss')}
          </span>
        </div>
        <div>
          <span className='timeLabel'>End Time: </span>
          <span style={{ fontSize: '1rem' }}>
            {dayjs(selectedDuration.endTime).format('DD-MMM-YY HH:mm:ss')}
          </span>
        </div>
      </TimeContainer>



      <EcgContentContainer>
        {ecgView === 'window' && (
          <Table
            columns={windowColumns(currentPage, pageSize)}
            dataSource={selectedRecordedEcgWindow?.patientRecordedEcgWindow}
            loading={false}
            rowKey={(record) => record.id}
            onRow={(record, index: any) => ({
              onClick: () => {
                let globalRowIndex = patientRecordedEcgWindowLength - ((currentPage - 1) * pageSize + index);
                sessionStorage.setItem('selected-ECG-SnapShotId', `ECG Snapshot-${globalRowIndex.toString()}`)
                setEcgView('signal')
                setSelectedWindow(record)
                console.log('setSelectedWindow ', record, index)
                if (
                  result.originalArgs?.ecgWindowId !== record.id &&
                  patientKey
                ) {
                  trigger({ patientKey, ecgWindowId: record.id })
                }
              },
            })}
            scroll={{ y: '0' }}
            className='pageDataTable clickable'
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              position: ['topLeft'],
              pageSizeOptions: [10, 20, 50],
              defaultPageSize: 10,
              showSizeChanger: true,
              total: selectedRecordedEcgWindow?.patientRecordedEcgWindow.length
            }}
            onChange={handleTableChange}
          />
        )}


        {selectedWindow && ecgView === 'signal' && (
          <EcgHeaderContainer>
            <div>
              <strong>Episode Snapshot: </strong>
              {sessionStorage.getItem('selected-ECG-SnapShotId')}
            </div>
            <div>
              <strong>Snapshot Start time: </strong>
              {dayjs.unix((selectedWindow.start_time / 1000)).format('DD-MMM-YY HH:mm:ss')}
            </div>
            <div>
              <strong>Snapshot End Time: </strong>
              {dayjs.unix((selectedWindow.end_time / 1000)).format('DD-MMM-YY HH:mm:ss')}
            </div>
          </EcgHeaderContainer>
        )}

        {ecgView === 'signal' &&
          (result.isFetching ? (
            <LoadingContainer>
              <LoadingContent>
                <Spin size='large' />
                <LoadingText>Loading ECG Signals...</LoadingText>
              </LoadingContent>
            </LoadingContainer>
          ) : (
            <EcgChartsContainer id='printable-signals' className='adds-grid'>
              <EcgChartWrapper>
                <SimpleChart signals={signals} offset={0} duration={8} />
              </EcgChartWrapper>
            </EcgChartsContainer>
          ))}
      </EcgContentContainer>
    </RecordedDataTabContainer>
  )
}